// =============================================================
// FLOW (a): Login → 6-digit MFA → home.
//
// Two-step against the REAL /api/v1: password (POST /auth/login) then, if the
// account has MFA, the 6-digit code (POST /auth/mfa/verify). On success the
// AuthContext loads /auth/me and the app routes to home.
// =============================================================

import { useState, type FormEvent } from "react";
import { useAuth } from "../app/AuthContext.js";
import { usePrefs } from "../app/PrefsContext.js";
import { useScopedT } from "../lib/i18n-keys.js";
import { Btn, Icon, ICONS } from "../components/primitives.js";

function errKey(code: string | null): string | null {
  if (!code) return null;
  return `login.err.${code}`;
}

export function Login() {
  const { phase, login, verifyMfa, error } = useAuth();
  const { theme } = usePrefs();
  const t = useScopedT();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const onPassword = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(username.trim(), password);
    } catch {
      /* error surfaced via context */
    } finally {
      setBusy(false);
    }
  };

  const onMfa = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await verifyMfa(code.trim());
    } catch {
      /* error surfaced via context */
    } finally {
      setBusy(false);
    }
  };

  const ek = errKey(error);
  // Fall back to a generic message when the specific key is unknown.
  const errText = ek ? t(ek) : null;
  const errShown = errText && errText !== ek ? errText : error ? t("login.err.network_error") : null;

  return (
    <div className="login-wrap">
      <div className="login-card">
        <div className="login-brand">
          <img src={theme === "dark" ? "/iterum-symbol-ondark.png" : "/iterum-symbol-multicolor.png"} alt="" />
          <span className="nm">
            {t("app.name").toLowerCase()}
            <span className="dot">.</span>
          </span>
        </div>

        {phase === "mfa" ? (
          <form onSubmit={onMfa}>
            <p className="login-sub">{t("login.mfaSub")}</p>
            <h2 style={{ marginBottom: 18, fontSize: 18 }}>{t("login.mfaTitle")}</h2>
            <div className="d-field mfa-digits">
              <label htmlFor="mfa">{t("login.mfaCode")}</label>
              <div className={`field-ctrl ${errShown ? "is-error" : ""}`}>
                <input
                  id="mfa"
                  inputMode="numeric"
                  maxLength={6}
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="••••••"
                />
              </div>
            </div>
            {errShown && (
              <div className="inline-alert error">
                <Icon d={ICONS.alert} size={14} />
                {errShown}
              </div>
            )}
            <Btn type="submit" variant="primary" size="core" disabled={busy || code.length !== 6} className="login-go">
              {t("login.mfaSubmit")}
            </Btn>
          </form>
        ) : (
          <form onSubmit={onPassword}>
            <p className="login-sub">{t("login.sub")}</p>
            <h2 style={{ marginBottom: 18, fontSize: 18 }}>{t("login.title")}</h2>
            <div className="d-field">
              <label htmlFor="u">{t("login.username")}</label>
              <div className="field-ctrl">
                <input
                  id="u"
                  autoFocus
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </div>
            </div>
            <div className="d-field">
              <label htmlFor="p">{t("login.password")}</label>
              <div className={`field-ctrl ${errShown ? "is-error" : ""}`}>
                <input
                  id="p"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>
            {errShown && (
              <div className="inline-alert error">
                <Icon d={ICONS.alert} size={14} />
                {errShown}
              </div>
            )}
            <Btn
              type="submit"
              variant="primary"
              size="core"
              disabled={busy || !username.trim() || !password}
              className="login-go"
            >
              {t("login.submit")}
            </Btn>
          </form>
        )}
      </div>
    </div>
  );
}
